// client/src/components/PublicProfile/PublicProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, AlertTriangle, Search, Trophy, Award, Target, Calendar, Crown, Globe } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

import PublicPlayerInfo from './PublicPlayerInfo';
import PublicStatistics from './PublicStatistics';
import PublicMatchHistory from './PublicMatchHistory';
import PublicBadges from './PublicBadges';
import PublicLeaderboard from './PublicLeaderboard';
import PublicProfileSearch from './PublicProfileSearch';
import './PublicProfile.css';

// Import enhanced translation system
import { translations, languageConfig, getTranslation, isRTL, getAvailableLanguages } from '../../translations/translations';

// Level calculation utility (same as original)
const calculateLevel = (totalExp) => {
  if (!totalExp || totalExp < 0) return 1;
  
  const LEVEL_STRUCTURE = [
    50, 50, 50, 50, 50, // Levels 1-5: 50 EXP each
    75, 75, 75, 75, 75, // Levels 6-10: 75 EXP each  
    100, 100, 100, 100, 100, 100, 100, 100, 100, 100, // Levels 11-20: 100 EXP each
    200, 200, 200, 200, 200 // Levels 21-25: 200 EXP each (partial)
  ];
  
  let currentLevel = 1;
  let expUsed = 0;
  
  for (let level = 1; level <= 100; level++) {
    const expNeededForThisLevel = LEVEL_STRUCTURE[level - 1] || (level <= 30 ? 200 : level <= 50 ? 300 : level <= 80 ? 500 : level <= 90 ? 1000 : 2000);
    
    if (expUsed + expNeededForThisLevel <= totalExp) {
      expUsed += expNeededForThisLevel;
      currentLevel = level + 1;
    } else {
      break;
    }
  }
  
  return Math.min(currentLevel, 100);
};

const PublicProfile = ({ user, currentLanguage: propCurrentLanguage, t: propT, onLanguageChange }) => {
  const { identifier } = useParams(); // username or ID
  const navigate = useNavigate();

  // Use propCurrentLanguage directly - App.js is the source of truth
  // Only fallback to localStorage if no prop provided (standalone mode)
  const currentLanguage = propCurrentLanguage || localStorage.getItem('preferred-language') || 'en';

  // State for language dropdown
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const availableLanguages = getAvailableLanguages();

  // Language change handler that syncs with parent component
  const handleLanguageSelect = (langCode) => {
    localStorage.setItem('preferred-language', langCode);
    setShowLangDropdown(false);
    // Sync with parent - this is the primary way to change language
    if (onLanguageChange) {
      onLanguageChange(langCode);
    }
  };

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSearch, setShowSearch] = useState(false);

  // Enhanced translation function with fallback
  const t = React.useCallback((key, fallback = null) => {
    // Use prop function if available
    if (propT) return propT(key, fallback);
    // Otherwise use our own translation function
    return getTranslation(key, currentLanguage, fallback);
  }, [propT, currentLanguage]);

  // Critical: Apply RTL layout on component mount and language change
  useEffect(() => {
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    // Set direction and language attributes
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    // Apply or remove RTL class
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    console.log('🌐 PublicProfile RTL setup:', {
      language: currentLanguage,
      isRtl,
      direction: htmlElement.getAttribute('dir'),
      hasRtlClass: htmlElement.classList.contains('rtl-layout')
    });
    
    // Language preference is saved in handleLanguageToggle
  }, [currentLanguage]);

  // Load profile data
  useEffect(() => {
    if (identifier) {
      loadProfile(identifier);
    } else {
      // No identifier means we're in search mode
      setShowSearch(true);
      setLoading(false);
      // Clear any previous referrer when going to search
      sessionStorage.removeItem('profileReferrer');
    }
  }, [identifier]);

  const loadProfile = async (userIdentifier) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading profile for:', userIdentifier);
      
      const response = await axios.get(`/api/public-profile/${userIdentifier}`);
      let profileDataRaw = response.data;
      
      console.log('📦 Raw profile data:', profileDataRaw);
      
      // Fix level calculation if needed
      if (profileDataRaw.totalExp && (!profileDataRaw.currentLevel || profileDataRaw.currentLevel === 1)) {
        const calculatedLevel = calculateLevel(profileDataRaw.totalExp);
        console.log(`🔧 Level fix: totalExp=${profileDataRaw.totalExp}, currentLevel=${profileDataRaw.currentLevel} -> ${calculatedLevel}`);
        profileDataRaw.currentLevel = calculatedLevel;
      }
      
      // Ensure currentLevel defaults to 1 if not set
      if (!profileDataRaw.currentLevel) {
        profileDataRaw.currentLevel = 1;
      }
      
      // Debug level display
      console.log('📊 Final profile data for level display:', {
        username: profileDataRaw.username,
        inGameName: profileDataRaw.inGameName,
        currentLevel: profileDataRaw.currentLevel,
        totalExp: profileDataRaw.totalExp,
        wins: profileDataRaw.wins,
        badgeCount: profileDataRaw.badgeCount
      });
      
      setProfileData(profileDataRaw);
      setShowSearch(false);
    } catch (error) {
      console.error('❌ Failed to load profile:', error);
      if (error.response?.status === 404) {
        setError(t('playerNotFound', 'Player not found'));
      } else if (error.response?.status === 403) {
        setError(t('profilePrivate', 'This profile is private'));
      } else {
        setError(t('failedToLoadProfile', 'Failed to load profile'));
      }
      toast.error(error.response?.data?.message || t('failedToLoadProfile', 'Failed to load profile'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchIdentifier) => {
    if (searchIdentifier) {
      // Mark that we're coming from search so back button works correctly
      sessionStorage.setItem('profileReferrer', 'search');
      navigate(`/profile/${searchIdentifier}`);
    }
  };

  const handleBack = () => {
    // Navigation Logic:
    // 1. If search modal is open and we have a profile, just close the modal
    // 2. If we're in pure search mode (no profile), go to main website
    // 3. If we came from search, return to profile search page
    // 4. If we came from leaderboard, return to leaderboard
    // 5. Otherwise, use browser back or go to main
    
    if (showSearch && profileData) {
      // Close search modal, stay on current profile
      setShowSearch(false);
      return;
    }
    
    if (showSearch || !profileData) {
      // In search mode without profile, go to main website
      navigate('/');
      return;
    }
    
    const referrer = sessionStorage.getItem('profileReferrer');
    if (referrer === 'search') {
      sessionStorage.removeItem('profileReferrer');
      navigate('/profile');
    } else if (referrer === 'leaderboard') {
      sessionStorage.removeItem('profileReferrer');
      navigate('/leaderboard');
    } else {
      // No specific referrer, use browser back or go to main
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/');
      }
    }
  };

  const isOwnProfile = user && profileData && user._id === profileData._id;

  if (loading) {
    return (
      <div className="public-profile-container">
        {/* Floating Action Buttons - Mobile */}
        <div className="floating-actions">
          <div className="floating-lang-wrapper">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="floating-btn lang-btn"
              title={t('language', 'Language')}
            >
              {languageConfig[currentLanguage]?.flag || '🌐'}
            </button>
            {showLangDropdown && (
              <div className="floating-lang-dropdown">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-name">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleBack}
            className="floating-btn back-btn"
            title={t('back', 'Back')}
          >
            <ArrowLeft />
          </button>
        </div>

        <div className="public-profile-header">
          <div className="header-top">
            <div className="header-info">
              <h1 className="public-profile-title">
                {t('loadingProfile', 'Loading Profile...')}
              </h1>
            </div>
            <div className="header-actions">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft className="w-4 h-4" />
                {t('back', 'Back')}
              </button>
              <div className="language-dropdown-wrapper">
                <button
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                  className="language-button"
                  title={t('language', 'Language')}
                >
                  <Globe className="w-4 h-4" />
                  {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
                </button>
                {showLangDropdown && (
                  <div className="language-dropdown">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                      >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-name">{lang.nativeName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="public-profile-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('loadingPlayerProfile', 'Loading player profile...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || showSearch) {
    return (
      <div className="public-profile-container">
        {/* Floating Action Buttons - Mobile */}
        <div className="floating-actions">
          <div className="floating-lang-wrapper">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="floating-btn lang-btn"
              title={t('language', 'Language')}
            >
              {languageConfig[currentLanguage]?.flag || '🌐'}
            </button>
            {showLangDropdown && (
              <div className="floating-lang-dropdown">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-name">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleBack}
            className="floating-btn back-btn"
            title={t('back', 'Back')}
          >
            <ArrowLeft />
          </button>
        </div>

        <div className="public-profile-header">
          <div className="header-top">
            <div className="header-info">
              <h1 className="public-profile-title">
                {t('playerProfiles', 'Player Profiles')}
              </h1>
            </div>
            <div className="header-actions">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft className="w-4 h-4" />
                {t('back', 'Back')}
              </button>
              <div className="language-dropdown-wrapper">
                <button
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                  className="language-button"
                  title={t('language', 'Language')}
                >
                  <Globe className="w-4 h-4" />
                  {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
                </button>
                {showLangDropdown && (
                  <div className="language-dropdown">
                    {availableLanguages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                      >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-name">{lang.nativeName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="public-profile-content">
          {error ? (
            <div className="error-state">
              <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-red-400 mb-2">{error}</h3>
              <p className="text-gray-400 mb-4">
                {error === t('playerNotFound', 'Player not found') 
                  ? t('playerNotFoundDesc', 'The player you\'re looking for doesn\'t exist or has been banned.')
                  : error === t('profilePrivate', 'This profile is private')
                  ? t('profilePrivateDesc', 'This player has made their profile private.')
                  : t('profileLoadError', 'There was an error loading the profile. Please try again.')
                }
              </p>
              <PublicProfileSearch onSearch={handleSearch} t={t} />
            </div>
          ) : (
            <div className="search-state">
              <Search className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">
                {t('findPlayer', 'Find a Player')}
              </h3>
              <p className="text-gray-400 mb-6">
                {t('searchPlayerDesc', 'Search for any player by their username or in-game name to view their public profile.')}
              </p>
              <PublicProfileSearch onSearch={handleSearch} t={t} />
            </div>
          )}
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'overview',
      label: t('overview', 'Overview'),
      icon: User
    },
    {
      id: 'statistics',
      label: t('statistics', 'Statistics'),
      icon: Trophy
    },
    {
      id: 'matches',
      label: t('matchHistory', 'Match History'),
      icon: Calendar
    },
    {
      id: 'badges',
      label: t('badges', 'Badges'),
      icon: Award
    },
    {
      id: 'leaderboard',
      label: t('leaderboard', 'Leaderboard'),
      icon: Crown
    }
  ];

  console.log('🎨 Rendering profile with data:', {
    inGameName: profileData.inGameName,
    currentLevel: profileData.currentLevel,
    wins: profileData.wins,
    badgeCount: profileData.badgeCount,
    isOnline: profileData.isOnline,
    currentLanguage,
    isRTL: isRTL(currentLanguage)
  });

  return (
    <div className="public-profile-container">
      {/* Floating Action Buttons - Mobile */}
      <div className="floating-actions">
        <div className="floating-lang-wrapper">
          <button
            onClick={() => setShowLangDropdown(!showLangDropdown)}
            className="floating-btn lang-btn"
            title={t('language', 'Language')}
          >
            {languageConfig[currentLanguage]?.flag || '🌐'}
          </button>
          {showLangDropdown && (
            <div className="floating-lang-dropdown">
              {availableLanguages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                >
                  <span className="lang-flag">{lang.flag}</span>
                  <span className="lang-name">{lang.nativeName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowSearch(true)}
          className="floating-btn search-btn"
          title={t('searchOtherPlayer', 'Search for another player')}
        >
          <Search />
        </button>
        <button
          onClick={handleBack}
          className="floating-btn back-btn"
          title={t('back', 'Back')}
        >
          <ArrowLeft />
        </button>
      </div>

      {/* Header */}
      <div className="public-profile-header">
        <div className="header-top">
          <div className="header-info">
            {isOwnProfile && (
              <span className="own-profile-badge">
                {t('yourProfile', 'Your Profile')}
              </span>
            )}
          </div>
          <div className="header-actions">
            <button onClick={handleBack} className="back-button">
              <ArrowLeft className="w-4 h-4" />
              {t('back', 'Back')}
            </button>
            <button
              onClick={() => setShowSearch(true)}
              className="search-button"
              title={t('searchOtherPlayer', 'Search for another player')}
            >
              <Search className="w-4 h-4" />
              {t('search', 'Search')}
            </button>
            <div className="language-dropdown-wrapper">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="language-button"
                title={t('language', 'Language')}
              >
                <Globe className="w-4 h-4" />
                {languageConfig[currentLanguage]?.flag} {languageConfig[currentLanguage]?.nativeName}
              </button>
              {showLangDropdown && (
                <div className="language-dropdown">
                  {availableLanguages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <span className="lang-name">{lang.nativeName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <h1 className="public-profile-title">
          {t('profileTitle', '{name}\'s Profile').replace('{name}', profileData.inGameName)}
        </h1>
        
        {/* Profile Quick Info */}
        <div className="profile-quick-info">
          <div className="quick-info-item">
            <Target className="w-4 h-4" />
            <span>
              {t('level', 'Level')} {profileData.currentLevel || 1}
              {profileData.totalExp && (
                <small style={{ marginLeft: '4px', opacity: 0.7 }}>
                  ({profileData.totalExp} {t('exp', 'EXP')})
                </small>
              )}
            </span>
          </div>
          <div className="quick-info-item">
            <Trophy className="w-4 h-4" />
            <span>{profileData.wins || 0} {t('wins', 'Wins')}</span>
          </div>
          <div className="quick-info-item">
            <Award className="w-4 h-4" />
            <span>{profileData.badgeCount || 0} {t('badges', 'Badges')}</span>
          </div>
          <div className="quick-info-item">
            <span className={`status-indicator ${profileData.isOnline ? 'online' : 'offline'}`}>
              {profileData.isOnline ? t('online', 'Online') : t('offline', 'Offline')}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="profile-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="public-profile-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="overview-main">
                <PublicPlayerInfo 
                  profileData={profileData} 
                  isOwnProfile={isOwnProfile}
                  t={t} 
                />
              </div>
              <div className="overview-sidebar">
                <PublicStatistics 
                  profileData={profileData}
                  compact={true}
                  t={t} 
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <PublicStatistics 
            profileData={profileData}
            t={t} 
          />
        )}

        {activeTab === 'matches' && (
          <PublicMatchHistory 
            profileData={profileData}
            t={t} 
          />
        )}

        {activeTab === 'badges' && (
          <PublicBadges
            profileData={profileData}
            t={t}
          />
        )}

        {activeTab === 'leaderboard' && (
          <PublicLeaderboard
            user={user}
            currentLanguage={currentLanguage}
            t={t}
          />
        )}
      </div>

      {/* Search Modal */}
      {showSearch && (
        <div className="search-modal-overlay" onClick={() => setShowSearch(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>{t('searchForPlayer', 'Search for a Player')}</h3>
              <button 
                onClick={() => setShowSearch(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <PublicProfileSearch 
              onSearch={(searchIdentifier) => {
                setShowSearch(false);
                handleSearch(searchIdentifier);
              }} 
              t={t} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile;